import PropTypes from 'prop-types'
import { trimFields } from '../../utils'
import { kebabCase } from 'lodash'
import { ModifiedModalFooter } from '../../elements'
import { Can } from '../../components'
import { Alert, Button, Form, Input, Modal, Popconfirm, Space } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'

const TextArea = Input.TextArea

export const DeleteButton = ({ onDelete }) => (
  <Can
    key="delete"
    requiredPermission="reverse_dc.delete.reverse_dc_id"
    yes={() => (
      <Popconfirm
        cancelText="No"
        className="action-list-button"
        okText="Yes"
        onConfirm={onDelete}
        title="Are you sure you want to delete this reverse dc?"
      >
        <Button
          danger
          data-testid="deleteBtn"
          icon={<DeleteOutlined />}
          name="deleteReverseDCModalSelenium"
          style={{ float: 'left' }}
        >
          Delete
        </Button>
      </Popconfirm>
    )}
  />
)

DeleteButton.propTypes = {
  isModalAnUpdate: PropTypes.bool,
  onDelete: PropTypes.func.isRequired,
}

export const ReverseDCModal = ({
  errorMessage,
  hideModal,
  isModalLoading,
  onDelete,
  onSubmit,
  selectedReverseDC,
  setModalLoading,
}) => {
  const [form] = Form.useForm()
  const isModalAnUpdate = !!selectedReverseDC
  const isOnlyView = isModalAnUpdate && selectedReverseDC.store_count > 0

  const handleSubmit = () => {
    const isFormUpdated = form.isFieldsTouched()

    if (isFormUpdated) {
      form.validateFields().then(() => {
        setModalLoading(true)

        const requiredFieldsValue = trimFields(form.getFieldsValue(['name', 'details']))
        const fieldsValue = {
          ...requiredFieldsValue,
          ...(isModalAnUpdate && { id: selectedReverseDC?.id }),
        }

        const actionType = isModalAnUpdate ? 'UPDATE' : 'CREATE'

        onSubmit(actionType, fieldsValue)
      })
    } else {
      hideModal()
    }
  }

  const handleNameChange = ({ target: { value } }) => {
    form.setFieldsValue({ slug: kebabCase(value) })
  }

  const initialValues = {
    details: selectedReverseDC?.details || '',
    name: selectedReverseDC?.name || '',
  }

  return (
    <Modal
      footer={[
        <ModifiedModalFooter key="padding">
          {isModalAnUpdate && !isOnlyView && <DeleteButton key="delete" onDelete={() => onDelete(selectedReverseDC)} />}

          <Space>
            <Button data-testid="cancelBtn" key="back" name="cancelReverseDCModalSelenium" onClick={hideModal}>
              Cancel
            </Button>

            <Can
              key={isModalAnUpdate ? 'update' : 'add'}
              requiredPermission={isModalAnUpdate ? 'reverse_dc.put.reverse_dc_id' : 'reverse_dc.post.create'}
              yes={() => (
                <Button
                  data-testid={isModalAnUpdate ? 'updateBtn' : 'addBtn'}
                  key="submit"
                  loading={isModalLoading}
                  name={isModalAnUpdate ? 'updateReverseDCModalSelenium' : 'addReverseDCSelenium'}
                  onClick={handleSubmit}
                  type="primary"
                >
                  {isModalAnUpdate ? 'Update' : 'Add'}
                </Button>
              )}
            />
          </Space>
        </ModifiedModalFooter>,
      ]}
      onCancel={hideModal}
      onOk={handleSubmit}
      open
      title={isModalAnUpdate ? 'Update Reverse DC' : 'Create Reverse DC'}
    >
      <Form data-test="reverse-dc-form" form={form} initialValues={initialValues} layout="vertical">
        <Form.Item
          label="Name"
          min={15}
          name="name"
          onChange={handleNameChange}
          rules={[{ message: 'Please provide a reverse dc name!', required: true }]}
        >
          <Input disabled={isOnlyView} />
        </Form.Item>

        <Form.Item label="Details" name="details">
          <TextArea />
        </Form.Item>
      </Form>

      {errorMessage && (
        <Alert description={errorMessage} showIcon style={{ marginTop: 20 }} title="Error" type="error" />
      )}
    </Modal>
  )
}

ReverseDCModal.propTypes = {
  errorMessage: PropTypes.node,
  hideModal: PropTypes.func.isRequired,
  isModalLoading: PropTypes.bool,
  onDelete: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  selectedReverseDC: PropTypes.object,
  setModalLoading: PropTypes.func.isRequired,
}

export default ReverseDCModal
